import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { firestoreService } from '../services/firestore.service';
import { cacheService } from '../services/cache.service';
import { createDebouncedSync, syncImmediate, cancelSync, SYNC_TIMING } from '../services/syncHelper';

// Maximum elements per canvas to control Firestore costs
export const MAX_ELEMENTS_PER_CANVAS = 50;

// ===== KEY GENERATION UTILITIES =====

/**
 * Get ISO week number for a date
 * @param {Date} date - Date to get week number for
 * @returns {number} - ISO week number (1-53)
 */
const getISOWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};

/**
 * Get period key for a specific view and date
 * @param {string} view - Calendar view type (day, week, month, year)
 * @param {Date} date - Date to generate key for
 * @returns {string|null} - Period key (e.g., '2026-01-28', '2026-W05', '2026-01', '2026')
 */
export const getCanvasKey = (view, date) => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;

    switch (view) {
        case 'day':
            // Format: YYYY-MM-DD
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        case 'week':
            // Format: YYYY-Www (ISO week number)
            return `${d.getFullYear()}-W${String(getISOWeek(d)).padStart(2, '0')}`;

        case 'month':
            // Format: YYYY-MM
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

        case 'year':
            // Format: YYYY
            return `${d.getFullYear()}`;

        default:
            return null;
    }
};

/**
 * Get composite key combining view and period
 * @param {string} view - Calendar view type
 * @param {Date} date - Date
 * @returns {string|null} - Composite key (e.g., 'month-2026-01')
 */
export const getCompositeKey = (view, date) => {
    const periodKey = getCanvasKey(view, date);
    return periodKey ? `${view}-${periodKey}` : null;
};

/**
 * Extract elements from Konva JSON format (for migration)
 * @param {object} konvaJSON - Konva stage JSON
 * @returns {Array} - Simplified element array
 */
export const extractElementsFromKonvaJSON = (konvaJSON) => {
    if (!konvaJSON?.children?.[0]?.children) return [];

    return konvaJSON.children[0].children
        .filter(item => item.attrs?.className === 'Image')
        .map(item => ({
            id: item.attrs.id,
            type: 'image',
            src: item.attrs.id?.split('#')[0] || item.attrs.src,
            x: item.attrs.x || 0,
            y: item.attrs.y || 0,
            width: item.attrs.width || 100,
            height: item.attrs.height || 100,
            rotation: item.attrs.rotation || 0
        }));
};

export const useCanvasStore = create(
    persist(
        (set, get) => ({
            // Canvas data indexed by composite key: "{view}-{periodKey}"
            // e.g., "day-2026-01-28", "week-2026-W05", "month-2026-01", "year-2026"
            canvases: {},

            // Track which canvases are being loaded (prevents duplicate fetches)
            loadingKeys: {},

            // Firestore sync state
            userId: null,
            isInitialized: false,

            // ===== INITIALIZATION =====

            /**
             * Initialize canvas store with user ID
             * @param {string} userId - Firebase user ID
             */
            initializeWithUser: (userId) => {
                set({ userId, isInitialized: true });
                // Canvases are loaded on-demand via loadCanvas()
            },

            /**
             * Clear user data on logout
             */
            clearUserData: () => {
                // Cancel all pending canvas syncs
                const { canvases } = get();
                Object.keys(canvases).forEach(key => cancelSync(`canvas-${key}`));
                set({ userId: null, isInitialized: false, canvases: {}, loadingKeys: {} });
            },

            // ===== CANVAS LOADING =====

            /**
             * Load canvas for a specific view and date
             * Uses cache-first strategy
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @returns {Promise<object|null>} - Canvas data or null
             */
            loadCanvas: async (view, date) => {
                const { userId, canvases, loadingKeys } = get();
                const compositeKey = getCompositeKey(view, date);
                const periodKey = getCanvasKey(view, date);

                if (!compositeKey) return null;

                // Return cached canvas if exists in store
                if (canvases[compositeKey]) {
                    return canvases[compositeKey];
                }

                // Prevent duplicate loads
                if (loadingKeys[compositeKey]) {
                    return null;
                }

                // Check memory cache
                const cached = cacheService.getCanvasEntry(view, periodKey);
                if (cached) {
                    set(state => ({
                        canvases: { ...state.canvases, [compositeKey]: cached }
                    }));
                    return cached;
                }

                // Load from Firestore
                if (userId) {
                    set(state => ({
                        loadingKeys: { ...state.loadingKeys, [compositeKey]: true }
                    }));

                    try {
                        const canvas = await firestoreService.getCanvasData(userId, view, periodKey);

                        set(state => ({
                            loadingKeys: { ...state.loadingKeys, [compositeKey]: false }
                        }));

                        if (canvas) {
                            set(state => ({
                                canvases: { ...state.canvases, [compositeKey]: canvas }
                            }));
                            cacheService.setCanvasEntry(view, periodKey, canvas);
                            return canvas;
                        }
                    } catch (error) {
                        console.error('Failed to load canvas from Firestore:', error);
                        set(state => ({
                            loadingKeys: { ...state.loadingKeys, [compositeKey]: false }
                        }));
                    }
                }

                return null;
            },

            // ===== CANVAS SAVING =====

            /**
             * Save canvas data for a specific view and date
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @param {Array} elements - Canvas elements
             * @returns {boolean} - Success status
             */
            saveCanvas: (view, date, elements) => {
                const compositeKey = getCompositeKey(view, date);
                if (!compositeKey) return false;

                // Enforce element limit
                if (elements.length > MAX_ELEMENTS_PER_CANVAS) {
                    console.warn(`Canvas element limit exceeded: ${elements.length}/${MAX_ELEMENTS_PER_CANVAS}`);
                    return false;
                }

                const canvasData = {
                    elements: elements,
                    version: 1,
                    updatedAt: new Date().toISOString()
                };

                set(state => ({
                    canvases: { ...state.canvases, [compositeKey]: canvasData }
                }));

                // Debounced sync to Firestore
                get().syncCanvas(view, date);

                return true;
            },

            /**
             * Sync canvas to Firestore (debounced)
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             */
            syncCanvas: (view, date) => {
                const { userId } = get();
                if (!userId) return;

                const compositeKey = getCompositeKey(view, date);
                const periodKey = getCanvasKey(view, date);

                // Get canvas at execution time (not creation time) to avoid stale closures
                createDebouncedSync(
                    `canvas-${compositeKey}`,
                    () => {
                        const canvas = get().canvases[compositeKey];
                        if (canvas) {
                            return firestoreService.saveCanvasData(userId, view, periodKey, canvas);
                        }
                        return Promise.resolve();
                    },
                    () => {
                        const canvas = get().canvases[compositeKey];
                        if (canvas) {
                            cacheService.setCanvasEntry(view, periodKey, canvas);
                        }
                    },
                    SYNC_TIMING.NORMAL  // 1 second debounce for drag operations
                )();
            },

            // ===== ELEMENT OPERATIONS =====

            /**
             * Add element to canvas
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @param {object} element - Element to add
             * @returns {{success: boolean, reason?: string}}
             */
            addElement: (view, date, element) => {
                const compositeKey = getCompositeKey(view, date);
                const canvas = get().canvases[compositeKey] || { elements: [] };

                if (canvas.elements.length >= MAX_ELEMENTS_PER_CANVAS) {
                    return { success: false, reason: 'limit_reached' };
                }

                const newElements = [...canvas.elements, element];
                get().saveCanvas(view, date, newElements);
                return { success: true };
            },

            /**
             * Update element in canvas
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @param {string} elementId - Element ID to update
             * @param {object} updates - Properties to update
             */
            updateElement: (view, date, elementId, updates) => {
                const compositeKey = getCompositeKey(view, date);
                const canvas = get().canvases[compositeKey];

                if (!canvas) return;

                const newElements = canvas.elements.map(el =>
                    el.id === elementId ? { ...el, ...updates } : el
                );

                get().saveCanvas(view, date, newElements);
            },

            /**
             * Remove element from canvas
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @param {string} elementId - Element ID to remove
             */
            removeElement: (view, date, elementId) => {
                const compositeKey = getCompositeKey(view, date);
                const canvas = get().canvases[compositeKey];

                if (!canvas) return;

                const newElements = canvas.elements.filter(el => el.id !== elementId);
                get().saveCanvas(view, date, newElements);
            },

            /**
             * Delete entire canvas for a view/date
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             */
            deleteCanvas: async (view, date) => {
                const { userId } = get();
                const compositeKey = getCompositeKey(view, date);
                const periodKey = getCanvasKey(view, date);

                if (!compositeKey) return;

                // Cancel any pending sync
                cancelSync(`canvas-${compositeKey}`);

                // Remove from local state
                set(state => {
                    const { [compositeKey]: _removed, ...rest } = state.canvases;
                    return { canvases: rest };
                });

                // Delete from Firestore immediately
                if (userId) {
                    await syncImmediate(
                        `canvas-delete-${compositeKey}`,
                        () => firestoreService.deleteCanvasData(userId, view, periodKey),
                        () => cacheService.removeCanvasEntry(view, periodKey)
                    );
                }
            },

            // ===== GETTERS =====

            /**
             * Get canvas data for a view/date
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @returns {object|null}
             */
            getCanvas: (view, date) => {
                const compositeKey = getCompositeKey(view, date);
                return get().canvases[compositeKey] || null;
            },

            /**
             * Get elements for a view/date
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @returns {Array}
             */
            getElements: (view, date) => {
                const canvas = get().getCanvas(view, date);
                return canvas?.elements || [];
            },

            /**
             * Get element count for a view/date
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @returns {number}
             */
            getElementCount: (view, date) => {
                return get().getElements(view, date).length;
            },

            /**
             * Check if more elements can be added
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @returns {boolean}
             */
            canAddElement: (view, date) => {
                return get().getElementCount(view, date) < MAX_ELEMENTS_PER_CANVAS;
            },

            /**
             * Check if canvas is loading
             * @param {string} view - Calendar view type
             * @param {Date} date - Date
             * @returns {boolean}
             */
            isLoading: (view, date) => {
                const compositeKey = getCompositeKey(view, date);
                return !!get().loadingKeys[compositeKey];
            },

            // ===== MIGRATION =====

            /**
             * Migrate canvas data from old localStorage format
             * @param {string} view - Calendar view type
             * @param {Date} date - Date to migrate TO
             * @returns {Promise<boolean>} - True if migration occurred
             */
            migrateFromLocalStorage: async (view, date) => {
                const compositeKey = getCompositeKey(view, date);

                // Don't migrate if canvas already exists
                if (get().canvases[compositeKey]) {
                    return false;
                }

                // Check old localStorage key
                const oldKey = `canvasState${view}`;
                const oldData = localStorage.getItem(oldKey);

                if (oldData) {
                    try {
                        const parsed = JSON.parse(oldData);
                        const elements = extractElementsFromKonvaJSON(parsed);

                        if (elements.length > 0) {
                            // Limit to max elements
                            const limitedElements = elements.slice(0, MAX_ELEMENTS_PER_CANVAS);
                            get().saveCanvas(view, date, limitedElements);
                            return true;
                        }
                    } catch (error) {
                        console.error('Migration failed:', error);
                    }
                }

                return false;
            },
        }),
        {
            name: 'grownups-canvas-storage',
            version: 1,
            partialize: (state) => ({
                canvases: state.canvases
            })
        }
    )
);
